// src/components/MainVideoPanel.jsx
import React, { useEffect, useRef } from "react";

const MainVideoPanel = (props) => {
  const {
    mainParticipant,
    pinnedId,
    setPinnedId,
    activeSpeakerId,
    remoteMediaState,
    remoteVideoRefs, // reserved for future use
    myStream,
    isMicOn,
    isCamOn,
    toggleMyMic,
    toggleMyCam,
    toggleRemoteTileAudio,   // not used here, but kept for compatibility
    toggleRemoteTileVideo,   // not used here, but kept for compatibility
    isScreenSharing,
    toggleScreenShare,
    handleExit,
    screenShareStreamRef,
    // either prop name from RoomPage is accepted
    isObserver,
    isAdminViewer,
  } = props;

  const mainVideoRef = useRef(null);

  const observerLike = !!(isObserver || isAdminViewer);
  const isActiveSpeaker =
    mainParticipant && activeSpeakerId === mainParticipant.id;
  const isLocalMain = !!(mainParticipant && mainParticipant.isLocal);

  // Pick main stream: screen share > participant stream
  const mainStream =
    (isScreenSharing && screenShareStreamRef?.current) ||
    mainParticipant?.stream ||
    null;

  // Per-media state for remote main
  const perMedia =
    !isLocalMain && mainParticipant
      ? remoteMediaState[mainParticipant.id] || {
          audioOn: true,
          videoOn: true,
        }
      : { audioOn: true, videoOn: true };

  const videoMuted = isLocalMain || !perMedia.audioOn;
  const videoHidden = !isLocalMain && !perMedia.videoOn;

  // Attach stream to <video>
  useEffect(() => {
    const el = mainVideoRef.current;
    if (!el || !mainStream) return;

    if (el.srcObject !== mainStream) {
      el.srcObject = mainStream;
    }

    el
      .play()
      .catch((err) =>
        console.warn("[MAIN] Error playing main video:", err?.message || err)
      );
  }, [mainStream]);

  const participantLabel = mainParticipant
    ? mainParticipant.isLocal
      ? "You"
      : mainParticipant.email || mainParticipant.id
    : "No one";

  // Surface CSS so hover-only overlay works with your existing <style>:
  //   .local-surface .local-controls { opacity:0; ... }
  //   .remote-surface .remote-controls { opacity:0; ... }
  const surfaceClass =
    "room-videoSurface " +
    (isActiveSpeaker ? "speaker-ring " : "") +
    (isLocalMain ? "local-surface" : "remote-surface");

  const overlayControlsClass =
    (isLocalMain ? "local-controls" : "remote-controls") +
    " room-overlayControls";

  return (
    <div className="room-mainVideoCard">
      {/* Header with pin, active chip, share + disconnect */}
      <div className="room-videoHeader">
        <div>
          <div className="room-videoTitle">
            {isScreenSharing
              ? "Screen share"
              : `Main speaker – ${participantLabel}`}
          </div>
          <div className="room-videoSubTitle">
            {observerLike
              ? "Observer view – your camera/mic are off"
              : isLocalMain
              ? "You are currently on the large view"
              : "Click a tile to pin someone here"}
          </div>
        </div>

        <div className="room-mainHeaderRight">
          {/* Screen share + leave – header, only for real participants */}
          {!observerLike && (
            <>
              <button
                type="button"
                className={
                  "room-controlBtn room-controlBtn--small" +
                  (isScreenSharing ? " room-controlBtn--active" : "")
                }
                onClick={toggleScreenShare}
                title={isScreenSharing ? "Stop screen share" : "Share screen"}
              >
                {isScreenSharing ? "⏹ Stop share" : "🖥 Share"}
              </button>

              <button
                type="button"
                className="room-controlBtn room-controlBtn--small room-controlBtn--danger"
                onClick={handleExit}
                title="Disconnect / leave room"
              >
                📞 Leave
              </button>
            </>
          )}

          {mainParticipant && (
            <button
              type="button"
              className={
                "room-pinBtn" +
                (pinnedId === mainParticipant.id ? " room-pinBtn--active" : "")
              }
              onClick={() =>
                setPinnedId(
                  pinnedId === mainParticipant.id ? null : mainParticipant.id
                )
              }
              title="Pin / unpin this participant"
            >
              📌
            </button>
          )}

          {isActiveSpeaker && (
            <div className="room-activeChip">Active speaker</div>
          )}
        </div>
      </div>

      {/* Video surface with hover-only overlay controls */}
      <div className={surfaceClass}>
        {mainStream ? (
          <video
            ref={mainVideoRef}
            className={
              "room-videoElement" +
              (isLocalMain && !isScreenSharing
                ? " room-videoElement--local"
                : "")
            }
            playsInline
            autoPlay
            muted={videoMuted}
            style={videoHidden ? { visibility: "hidden" } : undefined}
          />
        ) : (
          <div className="room-videoSurface--empty">
            Waiting for video…
          </div>
        )}

        {/* bottom-left overlay label */}
        {mainParticipant && (
          <div className="room-overlayLabel">{participantLabel}</div>
        )}

        {/* bottom-center overlay mic/cam – only on hover via CSS */}
        {!observerLike && mainStream && (
          <div className={overlayControlsClass}>
            <button
              type="button"
              className={
                "room-iconBtn" + (isMicOn ? " room-iconBtn--active" : "")
              }
              onClick={toggleMyMic}
              title={isMicOn ? "Turn mic off" : "Turn mic on"}
            >
              {isMicOn ? "🎙" : "🔇"}
            </button>

            <button
              type="button"
              className={
                "room-iconBtn" + (isCamOn ? " room-iconBtn--active" : "")
              }
              onClick={toggleMyCam}
              title={isCamOn ? "Turn camera off" : "Turn camera on"}
            >
              {isCamOn ? "📷" : "🚫"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MainVideoPanel;
